/**
 * Section IDs for `/cart/add` Section Rendering: same as cart-notification, plus optional
 * native `<cart-drawer>` (Dawn-style themes) so bundled JSON adds match standard PDP parity.
 */
function getCartAjaxSectionIds(cartNotification) {
  const ids = [];
  const pushId = (id) => {
    if (id && !ids.includes(id)) ids.push(id);
  };
  if (
    cartNotification &&
    typeof cartNotification.getSectionsToRender === "function"
  ) {
    cartNotification.getSectionsToRender().forEach((s) => pushId(s.id));
  }
  const drawer = document.querySelector("cart-drawer");
  if (drawer && typeof drawer.getSectionsToRender === "function") {
    drawer.getSectionsToRender().forEach((s) => pushId(s.id));
  }
  return ids;
}

function renderCartDrawerIfPresent(parsedState) {
  const drawer = document.querySelector("cart-drawer");
  if (!drawer || typeof drawer.renderContents !== "function" || !parsedState)
    return;
  try {
    drawer.renderContents(parsedState);
  } catch (_) {}
}

/** Monster Cart Upsell: sync drawer from Shopify cart without forcing it open (see MonsterApps developer docs). */
// function refreshMonsterCartIfPresent(open = false) {
//   try {
//     if (typeof window.monster_refresh === 'function') {
//       window.monster_refresh(open);
//     }
//   } catch (_) {}
// }

/** Shopify Section Rendering allows at most 5 sections per request (see shopify.dev Section Rendering API). */
const MAX_SECTIONS_PER_REQUEST = 5;

/** Section keys from `templates/cart.json` — rendered in `/cart` context so line items match the real cart. */
const CART_TEMPLATE_SECTION_IDS = ["cart-items", "cart-footer"];

function getCartPageUrl() {
  if (typeof routes !== "undefined" && routes.cart_url) {
    const u = String(routes.cart_url);
    return u.replace(/\/$/, "") || "/cart";
  }
  if (typeof routes !== "undefined" && routes.cart_add_url) {
    return String(routes.cart_add_url).replace(/\/cart\/add(\.js)?$/i, "/cart");
  }
  const root =
    typeof Shopify !== "undefined" &&
    Shopify.routes &&
    Shopify.routes.root != null
      ? String(Shopify.routes.root).replace(/\/?$/, "")
      : "";
  const path = `${root}/cart`.replace(/\/{2,}/g, "/");
  return path || "/cart";
}

function buildUrlWithSections(baseUrl, sectionIds) {
  const ids = (sectionIds || [])
    .filter(Boolean)
    .slice(0, MAX_SECTIONS_PER_REQUEST);
  if (!ids.length || !baseUrl) return "";
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}sections=${encodeURIComponent(ids.join(","))}`;
}

/** Replace a section node by matching the root element `id` from Section Rendering HTML (handles `template--…__cart-items`). */
function replaceRenderedSectionHtml(htmlString) {
  if (!htmlString || typeof htmlString !== "string") return;
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  const fresh = doc.body.firstElementChild;
  if (!fresh || !fresh.id) return;
  const cur = document.getElementById(fresh.id);
  if (!cur || !cur.parentNode) return;
  try {
    cur.replaceWith(document.importNode(fresh, true));
  } catch (_) {
    cur.replaceWith(fresh.cloneNode(true));
  }
}

function applySectionsFromJson(sections) {
  if (!sections || typeof sections !== "object") return;
  Object.keys(sections).forEach((key) =>
    replaceRenderedSectionHtml(sections[key]),
  );
}

/** Optional: theme / app blocks on the current URL whose section id looks cart-related. */
function discoverCartRelatedSectionIdsFromDom() {
  const re =
    /(drawer|slide|floating|mini|icart|upcart|vitals|cart(-|__)|ajax|apps)/i;
  const out = [];
  document.querySelectorAll('[id^="shopify-section-"]').forEach((el) => {
    const sid = el.id.slice("shopify-section-".length);
    if (re.test(`${sid} ${el.className || ""}`)) out.push(sid);
  });
  return [...new Set(out)].slice(0, MAX_SECTIONS_PER_REQUEST);
}

function fetchSectionsJson(url) {
  if (!url) return Promise.resolve({});
  return fetch(url, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
  })
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}));
}

function dispatchCartUpdatedFromServer(extraDetail) {
  const root =
    typeof Shopify !== "undefined" &&
    Shopify.routes &&
    Shopify.routes.root != null
      ? String(Shopify.routes.root)
      : "/";
  const base = root.endsWith("/") ? root : `${root}/`;
  fetch(`${base}cart.js`, { credentials: "same-origin" })
    .then((r) => r.json())
    .then((cart) => {
      const detail = {
        cart,
        ...(extraDetail && typeof extraDetail === "object" ? extraDetail : {}),
      };
      try {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:updated", { bubbles: true, detail }),
        );
      } catch (_) {}
      try {
        document.body.dispatchEvent(
          new CustomEvent("cart:refresh", { bubbles: true, detail }),
        );
      } catch (_) {}
    })
    .catch(() => {});
}

/**
 * After AJAX add: (1) refetch cart template sections from `/cart` so markup matches the cart;
 * (2) refetch a few cart-looking sections on the current page; (3) fire cart:updated with /cart.js.
 * See https://shopify.dev/docs/api/ajax/section-rendering (max 5 sections) and bundled section rendering on Cart API.
 */
function refreshSlideCartSectionsFromServer() {
  // refreshMonsterCartIfPresent(false);
  const cartPageUrl = getCartPageUrl();
  const cartSectionsUrl = buildUrlWithSections(
    cartPageUrl,
    CART_TEMPLATE_SECTION_IDS,
  );

  const currentPath = `${window.location.pathname}${window.location.search || ""}`;
  const domIds = discoverCartRelatedSectionIdsFromDom();
  const currentSectionsUrl = domIds.length
    ? buildUrlWithSections(currentPath, domIds)
    : "";

  const p1 = fetchSectionsJson(cartSectionsUrl);
  const p2 =
    currentSectionsUrl && currentSectionsUrl !== cartSectionsUrl
      ? fetchSectionsJson(currentSectionsUrl)
      : Promise.resolve({});

  Promise.all([p1, p2])
    .then(([fromCart, fromCurrent]) => {
      applySectionsFromJson(fromCart);
      applySectionsFromJson(fromCurrent);
      const mergedKeys = [
        ...Object.keys(fromCart || {}).filter((k) => fromCart[k]),
        ...Object.keys(fromCurrent || {}).filter((k) => fromCurrent[k]),
      ];
      dispatchCartUpdatedFromServer({ refreshedSectionKeys: mergedKeys });
    })
    .catch(() => dispatchCartUpdatedFromServer());
}

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        // Capture phase so we run before jQuery/Appstle bubble handlers that can add only the main line.
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this),
          true,
        );
        this.form.addEventListener(
          "click",
          this.onWalletClickCapture.bind(this),
          true,
        );
        // Form capture runs before button-level app handlers; apps often POST /cart/add.js on ATC click without submitting.
        this.form.addEventListener(
          "click",
          this.onFormClickCaptureForBundleAtc.bind(this),
          true,
        );
        this.cartNotification = document.querySelector("cart-notification");
        this.bundleInProgress = false;
      }

      cartAddJsUrl() {
        const u = routes.cart_add_url;
        return u.endsWith(".js") ? u : `${u.replace(/\/?$/, "")}.js`;
      }

      /**
       * Bundle when the add-on checkbox is checked. Prefer `data-bundle-add-on-variant-id` on
       * <product-form> (from Liquid) plus `.product-add-on__checkbox` — avoid querySelector(':checked')
       * and stray duplicate checkboxes without our class.
       */
      getBundleAddOnVariantId() {
        const expectedId = this.dataset.bundleAddOnVariantId;
        const infoId = this.dataset.productInfoId;
        const root =
          (infoId && document.getElementById(infoId)) ||
          this.closest(".product__info-container");
        if (!root) return null;

        const checkbox = root.querySelector(
          "input.product-add-on__checkbox[data-add-on-bundle-variant-id]",
        );
        if (!checkbox || !checkbox.checked) return null;

        const fromAttr = checkbox.getAttribute("data-add-on-bundle-variant-id");
        const raw = fromAttr || expectedId;
        if (expectedId && fromAttr && String(expectedId) !== String(fromAttr))
          return null;

        const id = parseInt(raw, 10);
        return Number.isFinite(id) ? id : null;
      }

      getMainQuantity() {
        const fd = new FormData(this.form);
        const q = parseInt(fd.get("quantity"), 10);
        return Number.isFinite(q) && q > 0 ? q : 1;
      }

      getMainVariantId() {
        return parseInt(this.form.querySelector("[name=id]").value, 10);
      }

      parseCartJsonResponse(response) {
        return response.text().then((text) => {
          try {
            return {
              ok: response.ok,
              status: response.status,
              data: JSON.parse(text),
            };
          } catch {
            return {
              ok: response.ok,
              status: response.status,
              data: {
                message: "Invalid JSON from cart",
                description: text.slice(0, 200),
              },
            };
          }
        });
      }

      isSuccessfulCartAddPayload(data) {
        return data && typeof data === "object" && Array.isArray(data.items);
      }

      /**
       * Build Shopify /cart/add.js `items` from the product form (variant, qty, selling plan, line properties).
       */
      buildMainLineItemFromForm() {
        const fd = new FormData(this.form);
        const id = parseInt(fd.get("id"), 10);
        if (!Number.isFinite(id)) return null;
        let quantity = parseInt(fd.get("quantity"), 10);
        if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;

        const item = { id, quantity };

        const sellingPlan = fd.get("selling_plan");
        if (sellingPlan != null && String(sellingPlan).trim() !== "") {
          const spId = parseInt(sellingPlan, 10);
          item.selling_plan = Number.isFinite(spId) ? spId : sellingPlan;
        }

        const properties = {};
        for (const [key, value] of fd.entries()) {
          if (!key.startsWith("properties[")) continue;
          const inner = key.match(/^properties\[(.+)\]$/);
          if (!inner) continue;
          const propKey = inner[1];
          if (value === "" || value == null) continue;
          properties[propKey] = String(value);
        }
        if (Object.keys(properties).length) item.properties = properties;

        return item;
      }

      /**
       * One POST adds main + add-on (atomic). Avoids apps that hijack the first request with `{ id, quantity }`.
       */
      runBundleAddSequence({ openNotification = true } = {}) {
        const addOnVariantId = this.getBundleAddOnVariantId();
        if (!addOnVariantId) {
          return Promise.reject(new Error("no-bundle"));
        }

        const mainVariantId = this.getMainVariantId();
        const mainItem = this.buildMainLineItemFromForm();
        if (!mainItem) {
          this.handleErrorMessage("Could not add to cart");
          return Promise.reject(new Error("main-item"));
        }

        const payload = {
          items: [mainItem, { id: addOnVariantId, quantity: 1 }],
        };
        if (this.cartNotification && openNotification) {
          const sectionIds = getCartAjaxSectionIds(this.cartNotification);
          if (sectionIds.length) {
            payload.sections = sectionIds.join(",");
            payload.sections_url = window.location.pathname;
          }
        }

        return fetch(this.cartAddJsUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(payload),
        })
          .then((r) => this.parseCartJsonResponse(r))
          .then(({ ok, status, data }) => {
            if (!this.isSuccessfulCartAddPayload(data)) {
              const msg =
                data.description ||
                data.message ||
                (!ok ? `Could not add to cart (HTTP ${status})` : "") ||
                "Could not add to cart";
              this.handleErrorMessage(msg);
              return Promise.reject(new Error("bundle-add"));
            }
            const hydrated = { ...data, id: mainVariantId };
            if (openNotification && this.cartNotification) {
              this.cartNotification.renderContents(hydrated);
            }
            renderCartDrawerIfPresent(hydrated);
            refreshSlideCartSectionsFromServer();
          });
      }

      onFormClickCaptureForBundleAtc(evt) {
        const btn = evt.target?.closest('button[type="submit"][name="add"]');
        if (!btn || !this.form.contains(btn)) return;
        if (!this.getBundleAddOnVariantId()) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        evt.stopPropagation();
        this.runAtcFlowAfterGesture();
      }

      clickOriginatedInWallet(evt) {
        if (
          evt.target.closest(
            ".shopify-payment-button, shopify-accelerated-checkout-cart, shopify-accelerated-checkout",
          )
        ) {
          return true;
        }
        if (typeof evt.composedPath !== "function") return false;
        return evt.composedPath().some((el) => {
          if (!el || el.nodeType !== 1) return false;
          const tag = el.tagName;
          return (
            tag === "SHOPIFY-ACCELERATED-CHECKOUT-CART" ||
            tag === "SHOPIFY-ACCELERATED-CHECKOUT" ||
            el.classList?.contains("shopify-payment-button")
          );
        });
      }

      onWalletClickCapture(evt) {
        if (!this.getBundleAddOnVariantId()) return;
        if (!this.clickOriginatedInWallet(evt)) return;

        evt.preventDefault();
        evt.stopPropagation();
        evt.stopImmediatePropagation();

        if (this.bundleInProgress) return;
        this.bundleInProgress = true;

        if (this.cartNotification) {
          this.cartNotification.setActiveElement(document.activeElement);
        }

        this.runBundleAddSequence({ openNotification: false })
          .then(() => {
            window.location.assign("/checkout");
          })
          .catch(() => {})
          .finally(() => {
            this.bundleInProgress = false;
          });
      }

      onSubmitHandler(evt) {
        const addOnVariantId = this.getBundleAddOnVariantId();

        if (addOnVariantId) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
        } else {
          evt.preventDefault();
        }

        this.runAtcFlowAfterGesture();
      }

      runAtcFlowAfterGesture() {
        const submitButton = this.querySelector('[type="submit"]');
        if (submitButton.classList.contains("loading")) return;
        if (this._pdpAtcPending) return;
        this._pdpAtcPending = true;

        const addOnVariantId = this.getBundleAddOnVariantId();

        this.handleErrorMessage();
        if (this.cartNotification) {
          this.cartNotification.setActiveElement(document.activeElement);
        }

        submitButton.setAttribute("aria-disabled", true);
        submitButton.classList.add("loading");
        this.querySelector(".loading-overlay__spinner").classList.remove(
          "hidden",
        );

        if (addOnVariantId) {
          this.runBundleAddSequence({
            openNotification: !!this.cartNotification,
          })
            .then(() => {
              if (!this.cartNotification) {
                window.location.assign("/cart");
              }
            })
            .catch(() => {})
            .finally(() => {
              this._pdpAtcPending = false;
              submitButton.classList.remove("loading");
              submitButton.removeAttribute("aria-disabled");
              this.querySelector(".loading-overlay__spinner").classList.add(
                "hidden",
              );
            });
          return;
        }

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cartNotification) {
          const sectionIds = getCartAjaxSectionIds(this.cartNotification);
          if (sectionIds.length) {
            formData.append("sections", sectionIds);
            formData.append("sections_url", window.location.pathname);
          }
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);
              return;
            }

            if (this.cartNotification) {
              this.cartNotification.renderContents(response);
              renderCartDrawerIfPresent(response);
              refreshSlideCartSectionsFromServer();
            } else {
              window.location.assign("/cart");
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this._pdpAtcPending = false;
            submitButton.classList.remove("loading");
            submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading-overlay__spinner").classList.add(
              "hidden",
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
